import { useState, useEffect } from 'react';
import { Admin } from '../types/admin';
import { TenantDetails } from './TenantDetails';
import { SuperAdminAccountTypes } from './SuperAdminAccountTypes';
import { AdminEditTenantModal } from './AdminEditTenantModal';
import { AdminAssistantTemplates } from './AdminAssistantTemplates';
import { AdminAnalytics } from './AdminAnalytics';
import { AdminAIMetrics } from './AdminAIMetrics';
import AdminAssistantsList from './AdminAssistantsList';
import { adminService, AdminTenant } from '../services/admin-service';
import { useToast } from '../hooks/use-toast';

interface AdminDashboardProps {
  admin: Admin;
  onLogout: () => void;
  onImpersonate?: (tenantId: string, userId: string) => void;
}

type AdminView = 'overview' | 'analytics' | 'ai-metrics' | 'assistants' | 'tenants' | 'users' | 'assistant-templates' | 'account-types' | 'ai-config' | 'settings';

interface Tenant {
  id: string;
  name: string;
  plan: 'Free' | 'Pro' | 'Enterprise';
  users: number;
  createdAt: Date;
  status: 'active' | 'inactive';
  totalMessages: number;
  avgMessagesPerUser: number;
  domain?: string;
  contactEmail?: string;
  phone?: string;
  address?: string;
  industry?: string;
  website?: string;
}

interface SystemUser {
  id: string;
  name: string;
  email: string;
  tenantId: string;
  role: 'user' | 'admin';
  status: 'active' | 'inactive';
  lastLogin: Date;
  totalMessages: number;
  avgMessagesPerDay: number;
}

interface AIConfig {
  providerId: string;
  name: string;
  enabled: boolean;
  plans: ('Free' | 'Pro' | 'Enterprise')[];
  models: string[];
  totalMessages: number;
  activeUsers: number;
  avgMessagesPerUser: number;
}

export function AdminDashboard({ admin, onLogout, onImpersonate }: AdminDashboardProps) {
  const [activeView, setActiveView] = useState<AdminView>('overview');
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [realTenants, setRealTenants] = useState<AdminTenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingTenant, setEditingTenant] = useState<AdminTenant | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [tenantFilter, setTenantFilter] = useState<'all' | 'active' | 'inactive'>('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userStatusFilter, setUserStatusFilter] = useState<'all' | 'active' | 'inactive'>('active');
  const [stats30Days, setStats30Days] = useState({
    active_tenants_30d: 0,
    active_users_30d: 0,
    total_messages_30d: 0
  });
  const [realAIMetrics, setRealAIMetrics] = useState<any[]>([]);
  const toast = useToast();

  // Carregar tenants reais do Supabase
  useEffect(() => {
    loadTenants();
    load30DaysStats();
    loadRealAIMetrics();
    if (activeView === 'users') {
      loadUsers();
    }
  }, [activeView]);

  const loadTenants = async () => {
    try {
      setIsLoading(true);
      const data = await adminService.getAllTenants();
      setRealTenants(data);
    } catch (error) {
      console.error('Erro ao carregar tenants:', error);
      toast.error('Erro ao carregar tenants');
    } finally {
      setIsLoading(false);
    }
  };

  const load30DaysStats = async () => {
    try {
      const data = await adminService.getLast30DaysStats();
      setStats30Days(data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas dos últimos 30 dias:', error);
    }
  };

  const loadRealAIMetrics = async () => {
    try {
      const data = await adminService.getAIMetrics(30);
      setRealAIMetrics(data);
    } catch (error) {
      console.error('Erro ao carregar métricas de IA:', error);
    }
  };

  const loadUsers = async () => {
    try {
      setIsLoadingUsers(true);
      const data = await adminService.getAllUsers();
      setAllUsers(data);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      toast.error('Erro ao carregar usuários');
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const exportUsersToCSV = () => {
    try {
      // Cabeçalho do CSV
      const headers = ['Nome', 'Email', 'Tenant ID', 'Tenant Nome', 'Role', 'Status', 'Criado em'];
      
      // Dados dos usuários filtrados
      const rows = filteredUsers.map(user => [
        user.name || '',
        user.email || '',
        user.tenant?.display_id || '',
        user.tenant?.name || '',
        user.role === 'owner' ? 'Owner' : user.role === 'manager' ? 'Manager' : 'User',
        user.status === 'active' ? 'Ativo' : user.status === 'suspended' ? 'Suspenso' : 'Inativo',
        new Date(user.created_at).toLocaleDateString('pt-BR')
      ]);
      
      // Criar CSV
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');
      
      // Criar blob e download
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `usuarios_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`${filteredUsers.length} usuários exportados com sucesso!`);
    } catch (error) {
      console.error('Erro ao exportar CSV:', error);
      toast.error('Erro ao exportar CSV');
    }
  };

  const handleEditTenant = (tenant: AdminTenant) => {
    setEditingTenant(tenant);
    setIsEditModalOpen(true);
  };

  const handleEditSuccess = () => {
    loadTenants(); // Recarregar lista
    toast.success('Tenant atualizado com sucesso!');
  };

  const handleImpersonateTenant = async (tenant: AdminTenant) => {
    if (!onImpersonate) {
      toast.error('Função de impersonation não disponível');
      return;
    }

    // Buscar o primeiro usuário owner/manager do tenant para impersonar
    try {
      const users = await adminService.getTenantUsers(tenant.id);
      const ownerOrManager = users.find(u => u.role === 'owner' || u.role === 'manager');
      
      if (!ownerOrManager) {
        toast.error('Nenhum administrador encontrado neste tenant');
        return;
      }

      onImpersonate(tenant.id, ownerOrManager.id);
    } catch (error) {
      console.error('Erro ao buscar usuários do tenant:', error);
      toast.error('Erro ao iniciar impersonation');
    }
  };

  const handleToggleStatus = async (tenant: AdminTenant) => {
    const newStatus = tenant.status === 'active' ? 'inactive' : 'active';
    const action = newStatus === 'active' ? 'reativar' : 'desativar';
    
    if (!confirm(`Tem certeza que deseja ${action} o tenant "${tenant.name}"?\n\n${newStatus === 'inactive' ? '⚠️ Os usuários deste tenant não poderão mais acessar a plataforma!' : '✅ Os usuários deste tenant poderão acessar a plataforma novamente.'}`)) {
      return;
    }

    try {
      await adminService.updateTenant(tenant.id, { status: newStatus });
      toast.success(`Tenant ${newStatus === 'active' ? 'reativado' : 'desativado'} com sucesso!`);
      loadTenants();
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      toast.error('Erro ao alterar status do tenant');
    }
  };

  // Filtrar tenants
  const filteredTenants = realTenants.filter(tenant => {
    // Filtro por status
    let matchesStatus = true;
    if (tenantFilter === 'active') matchesStatus = tenant.status === 'active';
    if (tenantFilter === 'inactive') matchesStatus = tenant.status !== 'active';
    
    // Filtro por busca (ID, nome ou domínio)
    let matchesSearch = true;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      matchesSearch = 
        tenant.display_id?.toString().includes(search) ||
        tenant.name.toLowerCase().includes(search) ||
        tenant.domain.toLowerCase().includes(search) ||
        tenant.id.toLowerCase().includes(search);
    }
    
    return matchesStatus && matchesSearch;
  });

  // Filtrar usuários
  const filteredUsers = allUsers.filter(user => {
    // Filtro por status
    let matchesStatus = true;
    if (userStatusFilter === 'active') matchesStatus = user.status === 'active';
    if (userStatusFilter === 'inactive') matchesStatus = user.status !== 'active';
    
    // Filtro por busca (nome, email ou tenant)
    let matchesSearch = true;
    if (userSearchTerm) {
      const search = userSearchTerm.toLowerCase();
      matchesSearch = 
        user.name?.toLowerCase().includes(search) ||
        user.email?.toLowerCase().includes(search) ||
        user.tenant?.name?.toLowerCase().includes(search) ||
        user.tenant?.display_id?.toString().includes(search);
    }
    
    return matchesStatus && matchesSearch;
  });

  // Mock data with message metrics (manter para outras views)
  const [tenants, setTenants] = useState<Tenant[]>([
    {
      id: '1',
      name: 'Empresa Alpha',
      plan: 'Enterprise',
      users: 25,
      createdAt: new Date('2024-01-15'),
      status: 'active',
      totalMessages: 15420,
      avgMessagesPerUser: 617,
      domain: 'alpha.com.br',
      contactEmail: 'contato@alpha.com.br',
      phone: '+55 11 99999-1111',
      address: 'São Paulo, SP',
      industry: 'Tecnologia',
      website: 'https://alpha.com.br'
    },
    {
      id: '2',
      name: 'Startup Beta',
      plan: 'Pro',
      users: 8,
      createdAt: new Date('2024-02-10'),
      status: 'active',
      totalMessages: 4200,
      avgMessagesPerUser: 525,
      domain: 'beta.com',
      contactEmail: 'hello@beta.com',
      phone: '+55 11 99999-2222',
      address: 'Rio de Janeiro, RJ',
      industry: 'E-commerce',
      website: 'https://beta.com'
    },
    {
      id: '3',
      name: 'Freelancer Gamma',
      plan: 'Free',
      users: 1,
      createdAt: new Date('2024-03-05'),
      status: 'active',
      totalMessages: 280,
      avgMessagesPerUser: 280,
      domain: 'gamma.dev',
      contactEmail: 'gamma@dev.com',
      phone: '+55 11 99999-3333',
      address: 'Belo Horizonte, MG',
      industry: 'Consultoria',
      website: 'https://gamma.dev'
    },
    {
      id: '4',
      name: 'Consultoria Delta',
      plan: 'Pro',
      users: 12,
      createdAt: new Date('2024-03-15'),
      status: 'inactive',
      totalMessages: 1850,
      avgMessagesPerUser: 154,
      domain: 'delta.com.br',
      contactEmail: 'contato@delta.com.br',
      phone: '+55 11 99999-4444',
      address: 'Brasília, DF',
      industry: 'Consultoria',
      website: 'https://delta.com.br'
    }
  ]);

  const [users, setUsers] = useState<SystemUser[]>([
    {
      id: '1',
      name: 'João Silva',
      email: 'joao@alpha.com',
      tenantId: '1',
      role: 'admin',
      status: 'active',
      lastLogin: new Date(),
      totalMessages: 1250,
      avgMessagesPerDay: 42
    },
    {
      id: '2',
      name: 'Maria Santos',
      email: 'maria@alpha.com',
      tenantId: '1',
      role: 'user',
      status: 'active',
      lastLogin: new Date('2024-01-20'),
      totalMessages: 850,
      avgMessagesPerDay: 28
    },
    {
      id: '3',
      name: 'Pedro Costa',
      email: 'pedro@beta.com',
      tenantId: '2',
      role: 'user',
      status: 'active',
      lastLogin: new Date('2024-01-21'),
      totalMessages: 920,
      avgMessagesPerDay: 31
    },
    {
      id: '4',
      name: 'Ana Oliveira',
      email: 'ana@gamma.com',
      tenantId: '3',
      role: 'user',
      status: 'active',
      lastLogin: new Date('2024-01-10'),
      totalMessages: 280,
      avgMessagesPerDay: 15
    },
    {
      id: '5',
      name: 'Carlos Ferreira',
      email: 'carlos@delta.com',
      tenantId: '4',
      role: 'admin',
      status: 'inactive',
      lastLogin: new Date('2024-01-05'),
      totalMessages: 120,
      avgMessagesPerDay: 4
    }
  ]);

  const [aiConfigs] = useState<AIConfig[]>([
    {
      providerId: 'claude',
      name: 'Claude (Anthropic)',
      enabled: true,
      plans: ['Pro', 'Enterprise'],
      models: ['Claude 3 Opus', 'Claude 3 Sonnet', 'Claude 3 Haiku'],
      totalMessages: 8540,
      activeUsers: 28,
      avgMessagesPerUser: 305
    },
    {
      providerId: 'gpt',
      name: 'GPT (OpenAI)',
      enabled: true,
      plans: ['Free', 'Pro', 'Enterprise'],
      models: ['GPT-5', 'GPT-4', 'GPT-3.5 Turbo'],
      totalMessages: 12350,
      activeUsers: 46,
      avgMessagesPerUser: 268
    },
    {
      providerId: 'gemini',
      name: 'Gemini (Google)',
      enabled: true,
      plans: ['Pro', 'Enterprise'],
      models: ['Gemini Pro', 'Gemini Pro Vision'],
      totalMessages: 1860,
      activeUsers: 15,
      avgMessagesPerUser: 124
    },
    {
      providerId: 'perplexity',
      name: 'Perplexity',
      enabled: false,
      plans: ['Enterprise'],
      models: ['Perplexity Pro', 'Perplexity Fast'],
      totalMessages: 0,
      activeUsers: 0,
      avgMessagesPerUser: 0
    }
  ]);

  const sidebarItems = [
    { id: 'overview', label: 'Visão Geral', icon: '📊' },
    { id: 'users', label: 'Usuários', icon: '👥' },
    { id: 'tenants', label: 'Tenants', icon: '🏢' },
    { id: 'analytics', label: 'Análise', icon: '📈' },
    { id: 'divider-1', label: '---', icon: '' },
    { id: 'ai-metrics', label: 'Custo por IA', icon: '💰' },
    { id: 'assistants', label: 'Custo por Agente', icon: '💵' },
    { id: 'divider-2', label: '---', icon: '' },
    { id: 'assistant-templates', label: 'Templates Agentes', icon: '📝' }
  ];

  const handleUpdateTenant = (updatedTenant: Tenant) => {
    setTenants(prev => 
      prev.map(tenant => 
        tenant.id === updatedTenant.id ? updatedTenant : tenant
      )
    );
    setSelectedTenant(null);
  };

  const handleUpdateUserStatus = (userId: string, status: 'active' | 'inactive') => {
    setUsers(prev => 
      prev.map(user => 
        user.id === userId ? { ...user, status } : user
      )
    );
  };

  const handleUpdateAIConfig = (tenantId: string, aiId: string, modelId: string, enabled: boolean) => {
    console.log(`Tenant ${tenantId}: AI ${aiId} modelo ${modelId} ${enabled ? 'habilitado' : 'desabilitado'}`);
  };

  const handleViewTenantDetails = (tenant: Tenant) => {
    console.log('🔍 Abrindo detalhes do tenant:', tenant.name);
    setSelectedTenant(tenant);
  };

  // Calcular estatísticas baseadas nos dados reais
  const tenantsWithStats = realTenants.map(t => ({
    ...t,
    users: t.user_count || 0,
    totalMessages: t.total_messages || 0
  }));
  
  const usersWithStats = allUsers.map(u => ({
    ...u,
    totalMessages: u.total_messages || 0
  }));
  
  const totalActiveUsers = tenantsWithStats.reduce((sum, t) => sum + t.users, 0);
  const totalMessages = tenantsWithStats.reduce((sum, t) => sum + t.totalMessages, 0);

  const renderOverview = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Visão Geral do Sistema</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <span className="text-blue-600 text-xl">🏢</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Tenants Ativos</p>
              <p className="text-2xl font-bold text-gray-900">{stats30Days.active_tenants_30d}</p>
              <p className="text-xs text-gray-500 mt-1">
                Últimos 30 dias
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <span className="text-green-600 text-xl">👥</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Usuários Ativos</p>
              <p className="text-2xl font-bold text-gray-900">{stats30Days.active_users_30d}</p>
              <p className="text-xs text-gray-500 mt-1">
                Últimos 30 dias
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <span className="text-purple-600 text-xl">💬</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Mensagens Trocadas</p>
              <p className="text-2xl font-bold text-gray-900">{stats30Days.total_messages_30d.toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-1">
                Últimos 30 dias
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Tenants com Maior Atividade</h3>
          <div className="space-y-3">
            {tenantsWithStats
              .sort((a, b) => b.totalMessages - a.totalMessages)
              .slice(0, 3)
              .map(tenant => (
                <div key={tenant.id} className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{tenant.name}</p>
                    <p className="text-sm text-gray-500">
                      Plano {tenant.plan} • {tenant.users} usuários
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-gray-900">{tenant.totalMessages.toLocaleString()}</p>
                    <p className="text-xs text-gray-500">mensagens</p>
                  </div>
                </div>
              ))}
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance das IAs</h3>
          <div className="space-y-3">
            {realAIMetrics.length > 0 ? (
              realAIMetrics
              .filter(ai => ai.enabled)
                .sort((a, b) => b.messages - a.messages)
              .map(ai => (
                <div key={ai.providerId} className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{ai.name}</p>
                    <p className="text-sm text-gray-500">
                        {ai.activeUsers} usuários ativos • 3 modelos
                    </p>
                  </div>
                  <div className="text-right">
                      <p className="font-bold text-gray-900">{ai.messages.toLocaleString()}</p>
                    <p className="text-xs text-gray-500">mensagens</p>
                  </div>
                </div>
                ))
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">
                Carregando métricas das IAs...
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderTenants = () => (
    <div className="space-y-6">
      <div className="flex flex-col gap-4">
      <div className="flex justify-between items-center">
          <div>
        <h2 className="text-2xl font-bold text-gray-900">Gestão de Tenants</h2>
            <p className="text-sm text-gray-500 mt-1">
              {filteredTenants.length} {filteredTenants.length === 1 ? 'tenant encontrado' : 'tenants encontrados'}
              {tenantFilter !== 'all' && ` (${tenantFilter === 'active' ? 'ativos' : 'inativos'})`}
            </p>
          </div>
          <button 
            onClick={loadTenants}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
          >
            🔄 Atualizar
        </button>
      </div>
      
        {/* Barra de busca e filtros */}
        <div className="flex items-center gap-3">
          {/* Campo de busca */}
          <div className="flex-1 max-w-md">
            <input
              type="text"
              placeholder="Buscar por ID, nome ou domínio..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          {/* Filtros */}
          <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setTenantFilter('active')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                tenantFilter === 'active'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              ✅ Ativos ({realTenants.filter(t => t.status === 'active').length})
            </button>
            <button
              onClick={() => setTenantFilter('inactive')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                tenantFilter === 'inactive'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              ❌ Inativos ({realTenants.filter(t => t.status !== 'active').length})
            </button>
            <button
              onClick={() => setTenantFilter('all')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                tenantFilter === 'all'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              📊 Todos ({realTenants.length})
            </button>
          </div>
        </div>
      </div>
      
      {isLoading ? (
        <div className="bg-white shadow border rounded-lg p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-500">Carregando tenants...</p>
        </div>
      ) : filteredTenants.length === 0 ? (
        <div className="bg-white shadow border rounded-lg p-12 text-center">
          <p className="text-gray-500">
            {tenantFilter === 'active' && 'Nenhum tenant ativo encontrado'}
            {tenantFilter === 'inactive' && 'Nenhum tenant inativo encontrado'}
            {tenantFilter === 'all' && 'Nenhum tenant encontrado'}
          </p>
        </div>
      ) : (
        <div className="bg-white shadow border rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plano</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuários</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mensagens</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Criado em</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
              {filteredTenants.map(tenant => (
                <tr key={tenant.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-md text-sm font-bold bg-blue-100 text-blue-800 font-mono">
                        #{tenant.display_id || '---'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">{tenant.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      tenant.plan === 'premium' ? 'bg-purple-100 text-purple-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {tenant.plan === 'premium' ? 'Premium' : 'Free'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      tenant.account_type === 'enterprise' ? 'bg-purple-100 text-purple-800' :
                      tenant.account_type === 'business' ? 'bg-blue-100 text-blue-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {tenant.account_type === 'enterprise' ? '👑 Enterprise' :
                       tenant.account_type === 'business' ? '🏢 Empresa' : 
                       '👤 Pessoal'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {tenant.user_count || 0}
                    {tenant.max_users && <span className="text-gray-400">/{tenant.max_users}</span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {(tenant.total_messages || 0).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      tenant.status === 'active' ? 'bg-green-100 text-green-800' :
                      tenant.status === 'suspended' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {tenant.status === 'active' ? 'Ativo' :
                       tenant.status === 'suspended' ? 'Suspenso' :
                       tenant.status === 'cancelled' ? 'Cancelado' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(tenant.created_at).toLocaleDateString('pt-BR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex flex-col space-y-2">
                      <button 
                        onClick={() => handleImpersonateTenant(tenant)}
                        className="inline-flex items-center px-3 py-1 border border-orange-600 text-xs font-medium rounded text-orange-600 bg-orange-50 hover:bg-orange-100"
                        title="Acessar como usuário deste tenant"
                      >
                        👁️ Impersonate
                      </button>
                      <button 
                        onClick={() => handleEditTenant(tenant)}
                        className="inline-flex items-center px-3 py-1 border border-green-600 text-xs font-medium rounded text-green-600 bg-green-50 hover:bg-green-100"
                      >
                        ✏️ Editar
                      </button>
                      <button 
                        onClick={() => handleToggleStatus(tenant)}
                        className={`inline-flex items-center px-3 py-1 border text-xs font-medium rounded ${
                        tenant.status === 'active' 
                            ? 'border-red-600 text-red-600 bg-red-50 hover:bg-red-100' 
                            : 'border-green-600 text-green-600 bg-green-50 hover:bg-green-100'
                        }`}
                      >
                        {tenant.status === 'active' ? '❌ Desativar' : '✅ Ativar'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      )}

      {/* Modal de Edição */}
      <AdminEditTenantModal
        tenant={editingTenant}
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingTenant(null);
        }}
        onSuccess={handleEditSuccess}
      />
    </div>
  );

  const renderUsers = () => (
    <div className="space-y-6">
      <div className="flex flex-col gap-4">
      <div className="flex justify-between items-center">
          <div>
        <h2 className="text-2xl font-bold text-gray-900">Gestão de Usuários</h2>
            <p className="text-sm text-gray-500 mt-1">
              {filteredUsers.length} {filteredUsers.length === 1 ? 'usuário encontrado' : 'usuários encontrados'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={exportUsersToCSV}
              disabled={filteredUsers.length === 0}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
            >
              📊 Exportar CSV
            </button>
            <button 
              onClick={loadUsers}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
            >
              🔄 Atualizar
        </button>
          </div>
      </div>
      
        {/* Barra de busca e filtros */}
        <div className="flex items-center gap-3">
          {/* Campo de busca */}
          <div className="flex-1 max-w-md">
            <input
              type="text"
              placeholder="Buscar por nome, email ou tenant..."
              value={userSearchTerm}
              onChange={(e) => setUserSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          {/* Filtros */}
          <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setUserStatusFilter('active')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                userStatusFilter === 'active'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              ✅ Ativos ({allUsers.filter(u => u.status === 'active').length})
            </button>
            <button
              onClick={() => setUserStatusFilter('inactive')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                userStatusFilter === 'inactive'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              ❌ Inativos ({allUsers.filter(u => u.status !== 'active').length})
            </button>
            <button
              onClick={() => setUserStatusFilter('all')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                userStatusFilter === 'all'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              📊 Todos ({allUsers.length})
            </button>
          </div>
        </div>
      </div>
      
      {isLoadingUsers ? (
        <div className="bg-white shadow border rounded-lg p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-500">Carregando usuários...</p>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="bg-white shadow border rounded-lg p-12 text-center">
          <p className="text-gray-500">
            {userStatusFilter === 'active' && 'Nenhum usuário ativo encontrado'}
            {userStatusFilter === 'inactive' && 'Nenhum usuário inativo encontrado'}
            {userStatusFilter === 'all' && 'Nenhum usuário encontrado'}
          </p>
        </div>
      ) : (
        <div className="bg-white shadow border rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tenant</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Criado em</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredUsers.map(user => (
              <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-gray-900">{user.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                  {user.tenant ? (
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-blue-100 text-blue-800 font-mono">
                        #{user.tenant.display_id}
                      </span>
                      <span className="text-sm text-gray-900">{user.tenant.name}</span>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">N/A</span>
                  )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    user.role === 'owner' ? 'bg-purple-100 text-purple-800' :
                    user.role === 'manager' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                    }`}>
                    {user.role === 'owner' ? 'Owner' :
                     user.role === 'manager' ? 'Manager' : 'User'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    user.status === 'active' ? 'bg-green-100 text-green-800' :
                    user.status === 'suspended' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {user.status === 'active' ? 'Ativo' :
                     user.status === 'suspended' ? 'Suspenso' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(user.created_at).toLocaleDateString('pt-BR')}
                  </td>
                </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );

  const renderAIConfig = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Configuração Global de IAs</h2>
      
      <div className="grid gap-6">
        {aiConfigs.map(ai => (
          <div key={ai.providerId} className="bg-white p-6 rounded-lg shadow border">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  ai.enabled ? 'bg-green-100' : 'bg-gray-100'
                }`}>
                  <span className="text-lg">🤖</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{ai.name}</h3>
                  <p className="text-sm text-gray-500">{ai.models.length} modelos disponíveis</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  ai.enabled 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {ai.enabled ? 'Ativo' : 'Inativo'}
                </span>
                <button className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50">
                  Configurar
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">Planos Disponíveis</h4>
                <div className="flex flex-wrap gap-2">
                  {ai.plans.map(plan => (
                    <span key={plan} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      plan === 'Enterprise' ? 'bg-purple-100 text-purple-800' :
                      plan === 'Pro' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {plan}
                    </span>
                  ))}
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">Total de Mensagens</h4>
                <div className="text-2xl font-bold text-gray-900">{ai.totalMessages.toLocaleString()}</div>
                <div className="text-sm text-gray-500">mensagens processadas</div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">Usuários Ativos</h4>
                <div className="text-2xl font-bold text-blue-600">{ai.activeUsers}</div>
                <div className="text-sm text-gray-500">usuários únicos</div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">Média por Usuário</h4>
                <div className="text-2xl font-bold text-green-600">{ai.avgMessagesPerUser}</div>
                <div className="text-sm text-gray-500">mensagens/usuário</div>
              </div>
            </div>

            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Modelos</h4>
              <div className="text-sm text-gray-600">
                {ai.models.join(', ')}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderSettings = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Configurações do Sistema</h2>
      
      <div className="grid gap-6">
        <div className="bg-white p-6 rounded-lg shadow border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Configurações Gerais</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-900">Registro de novos usuários</label>
              <button className="relative inline-flex h-6 w-11 items-center rounded-full bg-green-600">
                <span className="inline-block h-4 w-4 transform rounded-full bg-white transition translate-x-6" />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-900">Notificações por email</label>
              <button className="relative inline-flex h-6 w-11 items-center rounded-full bg-green-600">
                <span className="inline-block h-4 w-4 transform rounded-full bg-white transition translate-x-6" />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-900">Logs de auditoria</label>
              <button className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200">
                <span className="inline-block h-4 w-4 transform rounded-full bg-white transition translate-x-1" />
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Limites do Sistema</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Máximo de usuários por tenant (Free)
              </label>
              <input
                type="number"
                defaultValue="5"
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Máximo de usuários por tenant (Pro)
              </label>
              <input
                type="number"
                defaultValue="50"
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Limite de mensagens/mês (Free)
              </label>
              <input
                type="number"
                defaultValue="1000"
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Limite de mensagens/mês (Pro)
              </label>
              <input
                type="number"
                defaultValue="10000"
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          <div className="mt-6">
            <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
              Salvar Configurações
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderView = () => {
    switch (activeView) {
      case 'overview':
        return renderOverview();
      case 'analytics':
        return <AdminAnalytics />;
      case 'ai-metrics':
        return <AdminAIMetrics />;
      case 'assistants':
        return <AdminAssistantsList />;
      case 'tenants':
        return renderTenants();
      case 'users':
        return renderUsers();
      case 'assistant-templates':
        return <AdminAssistantTemplates />;
      case 'account-types':
        return <SuperAdminAccountTypes />;
      case 'ai-config':
        return renderAIConfig();
      case 'settings':
        return renderSettings();
      default:
        return renderOverview();
    }
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white text-sm">🛡️</span>
            </div>
            <h1 className="text-xl font-bold text-gray-900">SPACE</h1>
            <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold bg-blue-100 text-blue-800">
              Admin Panel
            </span>
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          <div className="text-right">
            <div className="text-sm font-medium">{admin.name}</div>
            <div className="text-xs text-gray-500 capitalize">{admin.role.replace('_', ' ')}</div>
          </div>
          <button
            onClick={onLogout}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            Sair
          </button>
        </div>
      </div>
      
      <div className="flex-1 flex">
        {/* Sidebar */}
        <div className="w-64 bg-white border-r border-gray-200">
          <nav className="mt-6 px-4">
            <div className="space-y-1">
              {sidebarItems.map(item => {
                // Renderizar divisor
                if (item.id.startsWith('divider')) {
                  return (
                    <div key={item.id} className="py-2">
                      <div className="border-t border-gray-200"></div>
                    </div>
                  );
                }
                
                // Renderizar item normal
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveView(item.id as AdminView)}
                    className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                      activeView === item.id
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    <span className="mr-3">{item.icon}</span>
                    {item.label}
                  </button>
                );
              })}
            </div>
          </nav>
        </div>
        
        {/* Main Content */}
        <main className="flex-1 p-6 overflow-auto">
          {renderView()}
        </main>
      </div>

      {/* Tenant Details Modal */}
      {selectedTenant && (
        <TenantDetails
          tenant={selectedTenant}
          users={users}
          onClose={() => setSelectedTenant(null)}
          onUpdateTenant={handleUpdateTenant}
          onUpdateUserStatus={handleUpdateUserStatus}
          onUpdateAIConfig={handleUpdateAIConfig}
        />
      )}
    </div>
  );
}